<template>
    <div class="mail-custom-v3 ppv2-relative">
        <!-- Header -->
        <Header :title="$t('mail_custom.title_page')" />

        <div class="ppv2-flex ppv2-flex-col ppv2-gap-6 ppv2-mb-24">
            <!-- Rappel avant réservation -->
            <SectionCard
                :title="$t('mail_custom.sections.reminder_before')"
                :value="localData.reminder_before_appointment_email"
                @input="(v) => (localData.reminder_before_appointment_email = v)"
            >
                <div class="ppv2-flex ppv2-flex-col" :style="{ gap: normalizeSize(16, true) }">
                    <div>
                        <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1">
                            {{ $t('mail_custom.fields.partial_email_description') }}
                            <span class="ppv2-text-primary-600">*</span>
                        </label>
                        <BaseTextarea
                            v-model="localData.appointment_reminder_text"
                            :use-editor="true"
                            :show-improve-with-a-i="true"
                            :icon-i-a="true"
                            :rows="10"
                        />
                    </div>
                    <div class="ppv2-w-full lg:ppv2-w-1/3">
                        <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1">
                            {{ $t('mail_custom.fields.reminder_days') }}
                            <span class="ppv2-text-primary-600">*</span>
                        </label>
                        <BaseInput
                            v-model="localData.appointment_reminder_time"
                            type="number"
                            :min="0"
                            placeholder="0"
                            class="ppv2-w-full ppv2-rounded-lg ppv2-border-gray-300 dark:ppv2-bg-dark-light dark:ppv2-border-gray-600"
                        />
                    </div>
                </div>
            </SectionCard>

            <!-- Confirmation de réservation -->
            <SectionCard
                :title="$t('mail_custom.sections.confirmation')"
                :value="localData.thanks_email_active"
                @input="(v) => (localData.thanks_email_active = v)"
            >
                <div>
                    <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1">
                        {{ $t('mail_custom.fields.partial_email_description') }}
                        <span class="ppv2-text-primary-600">*</span>
                    </label>
                    <BaseTextarea
                        v-model="localData.thanks_text"
                        :use-editor="true"
                        :show-improve-with-a-i="true"
                        :icon-i-a="true"
                        :rows="10"
                    />
                </div>
            </SectionCard>

            <!-- Demande de réservation avec empreinte -->
            <SectionCard
                :title="$t('mail_custom.sections.request_imprint')"
                :value="localData.reservation_request_active"
                @input="(v) => (localData.reservation_request_active = v)"
            >
                <div>
                    <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1">
                        {{ $t('mail_custom.fields.full_email_description') }}
                        <span class="ppv2-text-primary-600">*</span>
                    </label>
                    <BaseTextarea
                        v-model="localData.reservation_request_text"
                        :use-editor="true"
                        :show-improve-with-a-i="true"
                        :icon-i-a="true"
                        :rows="10"
                    />
                </div>
            </SectionCard>

            <!-- Remerciements après une visite -->
            <SectionCard
                :title="$t('mail_custom.sections.thanks_after')"
                :value="localData.thanks_after_visit_active"
                @input="(v) => (localData.thanks_after_visit_active = v)"
            >
                <div class="ppv2-flex ppv2-flex-col" :style="{ gap: normalizeSize(16, true) }">
                    <div>
                        <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1">
                            {{ $t('mail_custom.fields.partial_email_description') }}
                            <span class="ppv2-text-primary-600">*</span>
                        </label>
                        <BaseTextarea
                            v-model="localData.thanks_text_after_appointment"
                            :use-editor="true"
                            :show-improve-with-a-i="true"
                            :icon-i-a="true"
                            :rows="10"
                        />
                    </div>
                    <div class="ppv2-w-full lg:ppv2-w-1/3">
                        <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1">
                            {{ $t('mail_custom.fields.thanks_after_visit_time') }}
                            <span class="ppv2-text-primary-600">*</span>
                        </label>
                        <BaseInput
                            v-model="localData.thanks_after_visit_time"
                            type="number"
                            :min="0"
                            placeholder="0"
                            class="ppv2-w-full ppv2-rounded-lg ppv2-border-gray-300 dark:ppv2-bg-dark-light dark:ppv2-border-gray-600"
                        />
                    </div>
                    <div>
                        <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1">
                            {{ $t('mail_custom.fields.notice_google_link') }}
                        </label>
                        <BaseInput
                            v-model="localData.notice_google"
                            type="url"
                            placeholder="https://g.page/r/..."
                            class="ppv2-w-full ppv2-rounded-lg ppv2-border-gray-300 dark:ppv2-bg-dark-light dark:ppv2-border-gray-600"
                        />
                    </div>
                </div>
            </SectionCard>

            <!-- Acceptation / Refus (si pas en mode request) -->
            <template v-if="!data.establishment.reservation_request">
                <SectionCard :title="$t('mail_custom.sections.accept')" :hide-toggle="true">
                    <div>
                        <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1">
                            {{ $t('mail_custom.fields.partial_email_description') }}
                            <span class="ppv2-text-primary-600">*</span>
                        </label>
                        <BaseTextarea
                            v-model="localData.accept_reservation_request_text"
                            :use-editor="true"
                            :show-improve-with-a-i="true"
                            :icon-i-a="true"
                            :rows="10"
                        />
                    </div>
                </SectionCard>

                <SectionCard
                    :title="$t('mail_custom.sections.decline')"
                    :value="localData.decline_reservation_request_text"
                    @input="(v) => (localData.decline_reservation_request_text = v)"
                >
                    <div>
                        <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1">
                            {{ $t('mail_custom.fields.partial_email_description') }}
                            <span class="ppv2-text-primary-600">*</span>
                        </label>
                        <BaseTextarea
                            v-model="localData.decline_reservation_request_text"
                            :use-editor="true"
                            :show-improve-with-a-i="true"
                            :icon-i-a="true"
                            :rows="10"
                        />
                    </div>
                </SectionCard>
            </template>

            <!-- Cadeau d'anniversaire -->
            <SectionCard
                :title="$t('mail_custom.sections.birthday')"
                :value="localData.reminder_before_birthday_email"
                @input="(v) => (localData.reminder_before_birthday_email = v)"
            >
                <div class="ppv2-flex ppv2-flex-col" :style="{ gap: normalizeSize(16, true) }">
                    <div>
                        <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1">
                            {{ $t('mail_custom.fields.full_email_description') }}
                            <span class="ppv2-text-primary-600">*</span>
                        </label>
                        <BaseTextarea
                            v-model="localData.birthday_text"
                            :use-editor="true"
                            :show-improve-with-a-i="true"
                            :icon-i-a="true"
                            :rows="10"
                        />
                    </div>
                    <div class="ppv2-w-full lg:ppv2-w-1/3">
                        <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1">
                            {{ $t('mail_custom.fields.birthday_send_before_days') }}
                            <span class="ppv2-text-primary-600">*</span>
                        </label>
                        <BaseInput
                            v-model="localData.birthday_send_before_days"
                            type="number"
                            :min="0"
                            placeholder="0"
                            class="ppv2-w-full ppv2-rounded-lg ppv2-border-gray-300 dark:ppv2-bg-dark-light dark:ppv2-border-gray-600"
                        />
                    </div>
                    <div class="ppv2-w-full lg:ppv2-w-1/3">
                        <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1">
                            {{ $t('mail_custom.fields.birthday_points') }}
                            <span class="ppv2-text-primary-600">*</span>
                        </label>
                        <BaseInput
                            v-model="localData.birthday_points"
                            type="number"
                            :min="0"
                            placeholder="0"
                            class="ppv2-w-full ppv2-rounded-lg ppv2-border-gray-300 dark:ppv2-bg-dark-light dark:ppv2-border-gray-600"
                        />
                    </div>
                </div>
            </SectionCard>

            <!-- Palier de fidélité -->
            <SectionCard
                :title="$t('mail_custom.sections.fidelity')"
                :value="localData.points_treshold_active"
                @input="(v) => (localData.points_treshold_active = v)"
            >
                <div class="ppv2-flex ppv2-flex-col" :style="{ gap: normalizeSize(16, true) }">
                    <div>
                        <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1">
                            {{ $t('mail_custom.fields.full_email_description') }}
                            <span class="ppv2-text-primary-600">*</span>
                        </label>
                        <BaseTextarea
                            v-model="localData.smsTextPointsTreshold"
                            :use-editor="true"
                            :show-improve-with-a-i="true"
                            :icon-i-a="true"
                            :rows="10"
                        />
                    </div>
                    <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-3" :style="{ gap: normalizeSize(16, true) }">
                        <div>
                            <label
                                class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1"
                            >
                                {{ $t('mail_custom.fields.treshold_1') }}
                                <span class="ppv2-text-primary-600">*</span>
                            </label>
                            <BaseInput
                                v-model="localData.points_treshold_1"
                                type="number"
                                :min="0"
                                placeholder="0"
                                class="ppv2-w-full ppv2-rounded-lg ppv2-border-gray-300 dark:ppv2-bg-dark-light dark:ppv2-border-gray-600"
                            />
                        </div>
                        <div>
                            <label
                                class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1"
                            >
                                {{ $t('mail_custom.fields.treshold_2') }}
                                <span class="ppv2-text-primary-600">*</span>
                            </label>
                            <BaseInput
                                v-model="localData.points_treshold_2"
                                type="number"
                                :min="0"
                                placeholder="0"
                                class="ppv2-w-full ppv2-rounded-lg ppv2-border-gray-300 dark:ppv2-bg-dark-light dark:ppv2-border-gray-600"
                            />
                        </div>
                        <div>
                            <label
                                class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1"
                            >
                                {{ $t('mail_custom.fields.treshold_3') }}
                                <span class="ppv2-text-primary-600">*</span>
                            </label>
                            <BaseInput
                                v-model="localData.points_treshold_3"
                                type="number"
                                :min="0"
                                placeholder="0"
                                class="ppv2-w-full ppv2-rounded-lg ppv2-border-gray-300 dark:ppv2-bg-dark-light dark:ppv2-border-gray-600"
                            />
                        </div>
                    </div>
                </div>
            </SectionCard>

            <!-- Vous nous manquez -->
            <SectionCard
                :title="$t('mail_custom.sections.miss_you')"
                :value="localData.without_revisit_time_email"
                @input="(v) => (localData.without_revisit_time_email = v)"
            >
                <div class="ppv2-flex ppv2-flex-col" :style="{ gap: normalizeSize(16, true) }">
                    <div>
                        <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1">
                            {{ $t('mail_custom.fields.full_email_description') }}
                            <span class="ppv2-text-primary-600">*</span>
                        </label>
                        <BaseTextarea
                            v-model="localData.smsTextWithoutRevisitTime"
                            :use-editor="true"
                            :show-improve-with-a-i="true"
                            :icon-i-a="true"
                            :rows="10"
                        />
                    </div>
                    <div class="ppv2-w-full lg:ppv2-w-1/3">
                        <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-mb-1">
                            {{ $t('mail_custom.fields.revisit_time') }}
                            <span class="ppv2-text-primary-600">*</span>
                        </label>
                        <BaseInput
                            v-model="localData.without_revisit_time"
                            type="number"
                            :min="0"
                            placeholder="0"
                            class="ppv2-w-full ppv2-rounded-lg ppv2-border-gray-300 dark:ppv2-bg-dark-light dark:ppv2-border-gray-600"
                        />
                    </div>
                </div>
            </SectionCard>

            <!-- Paramètres des notifications -->
            <SectionCard :title="$t('mail_custom.sections.notification_settings')" :hide-toggle="true">
                <div class="ppv2-flex ppv2-flex-col ppv2-gap-6">
                    <div v-for="notif in notificationSettings" :key="notif.key" class="ppv2-flex ppv2-flex-col ppv2-gap-2">
                        <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                            <BaseSwitch v-model="localData[notif.key]" :label="notif.label" size="sm" />
                        </div>
                        <div v-if="localData[notif.key]" class="ppv2-ml-8">
                            <label class="ppv2-block ppv2-text-xs poppinsMedium ppv2-text-gray-500 ppv2-mb-1">
                                {{ $t('mail_custom.fields.select_roles') }}
                            </label>
                            <BaseSelect
                                v-model="localData[getRoleKey(notif.key)]"
                                :options="notif.data"
                                :multiple="true"
                                :placeholder="$t('mail_custom.fields.placeholder_roles')"
                            />
                        </div>
                    </div>
                </div>
            </SectionCard>
        </div>

        <!-- Floating Save Button -->
        <div class="ppv2-fixed ppv2-bottom-10 ppv2-right-10 ppv2-z-50">
            <BaseButton
                variant="primary"
                :loading="saving"
                @click="save"
                custom-class="!ppv2-rounded-xl !ppv2-px-8 !ppv2-py-4 ppv2-shadow-2xl"
            >
                {{ saving ? $t('common.saving') : $t('common.save') }}
            </BaseButton>
        </div>
    </div>
</template>

<script>
import normalizeSize from '../../../../utils/normalizeSize'

import Header from '../../../Shared/ConfigurationHeader.vue'
import SectionCard from '../../Website/Settings/components/SectionCard.vue'
import BaseTextarea from '../../../Shared/BaseTextarea.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import BaseInput from '../../../Shared/BaseInput.vue'

export default {
    name: 'BoMailCustomV3Index',
    components: {
        SectionCard,
        BaseTextarea,
        BaseButton,
        BaseSwitch,
        BaseSelect,
        BaseInput,
        Header,
    },
    props: {
        data: {
            type: Object,
            required: true,
        },
    },
    data() {
        const mails = this.data.establishment.mails_customization || {}
        const est = this.data.establishment

        // Initialize roles
        const initialRoles = {}
        const roleTypes = [
            'sellMail',
            'bookingMail',
            'bookingCancelMail',
            'booking_update_mail',
            'supplierOrderMail',
            'failBankImprintMail',
            'contreIndicationMail',
        ]

        roleTypes.forEach((type) => {
            const roleKey = type.includes('_') ? type + '_roles' : type + 'Roles'
            initialRoles[roleKey] = this.data.mailsRoles
                .filter((r) => r.pivot.mailType === type && r.name !== 'client')
                .map((r) => r.id)
        })

        return {
            saving: false,
            localData: {
                // Toggles & Fields
                reminder_before_appointment_email: !!mails.reminder_before_appointment_email,
                appointment_reminder_text: mails.appointment_reminder_text || '',
                appointment_reminder_time: mails.appointment_reminder_time || 0,

                reminder_before_birthday_email: !!mails.reminder_before_birthday_email,
                birthday_text: mails.birthday_text || '',
                birthday_points: mails.birthday_points || 0,
                birthday_send_before_days: mails.birthday_send_before_days || 0,

                thanks_text: mails.thanks_text || '',
                thanks_email_active: !!mails.thanks_email_active,

                reservation_request_text: mails.reservation_request_text || '',
                reservation_request_active: !!mails.reservation_request_active,

                thanks_text_after_appointment: mails.thanks_text_after_appointment || '',
                thanks_after_visit_active: !!mails.thanks_after_visit_active,
                thanks_after_visit_time: mails.thanks_after_visit_time || 3,
                notice_google: mails.notice_google || '',

                accept_reservation_request_text: mails.accept_reservation_request_text || '',
                decline_reservation_request_text: mails.decline_reservation_request_text || '',

                smsTextPointsTreshold: mails.smsTextPointsTreshold || '',
                points_treshold_active: !!mails.points_treshold_active,

                points_treshold_1: mails.points_treshold_1 || 0,
                points_treshold_2: mails.points_treshold_2 || 0,
                points_treshold_3: mails.points_treshold_3 || 0,

                without_revisit_time_email: !!mails.without_revisit_time_email,
                smsTextWithoutRevisitTime: mails.smsTextWithoutRevisitTime || '',
                without_revisit_time: mails.without_revisit_time || 30,

                // Notifications
                sellMail: !!est.sellMail,
                bookingMail: !!est.bookingMail,
                bookingCancelMail: !!est.bookingCancelMail,
                booking_update_mail: !!est.booking_update_mail,
                supplierOrderMail: !!est.supplierOrderMail,
                failBankImprintMail: !!est.failBankImprintMail,
                contreIndicationMail: !!est.contreIndicationMail,
                ...initialRoles,
            },
            notificationSettings: [
                {
                    key: 'sellMail',
                    label: 'Nouvelle vente — Notifier les rôles suivants',
                    data: this.data.allRoles
                        .filter((r) => r.name !== 'client')
                        .map((r) => ({ value: r.id, label: r.display_name })),
                },
                {
                    key: 'bookingMail',
                    label: 'Nouvelle réservation — Notifier les rôles suivants',
                    data: this.data.allRoles
                        .filter((r) => r.name !== 'client')
                        .map((r) => ({ value: r.id, label: r.display_name })),
                },
                {
                    key: 'bookingCancelMail',
                    label: 'Annulation de réservation — Notifier les rôles suivants',
                    data: this.data.allRoles
                        .filter((r) => r.name !== 'client')
                        .map((r) => ({ value: r.id, label: r.display_name })),
                },
                {
                    key: 'booking_update_mail',
                    label:
                        this.$t('mail_custom.fields.booking_update_mail') ||
                        'Modification de réservation — Notifier les rôles suivants',
                    data: this.data.allRoles
                        .filter((r) => r.name !== 'client')
                        .map((r) => ({ value: r.id, label: r.display_name })),
                },
                //{ key: 'supplierOrderMail', label: 'Commande fournisseur — Notifier les rôles suivants', data: this.data.mailsRoles.filter((r) => r.pivot.mailType === 'supplierOrderMail').map((r) => ({ value: r.id, label: r.display_name })) },
                //{ key: 'failBankImprintMail', label: 'Empreinte bancaire échouée — Notifier les rôles suivants', data: this.data.mailsRoles.filter((r) => r.pivot.mailType === 'failBankImprintMail').map((r) => ({ value: r.id, label: r.display_name })) },
                //{ key: 'contreIndicationMail', label: 'Contre-indication — Notifier les rôles suivants', data: this.data.mailsRoles.filter((r) => r.pivot.mailType === 'contreIndicationMail').map((r) => ({ value: r.id, label: r.display_name })) },
            ],
        }
    },
    methods: {
        normalizeSize,
        getRoleKey(type) {
            return type.includes('_') ? type + '_roles' : type + 'Roles'
        },
        async save() {
            this.saving = true
            try {
                const allNotificationTypes = [
                    'sellMail',
                    'bookingMail',
                    'bookingCancelMail',
                    'booking_update_mail',
                    'supplierOrderMail',
                    'failBankImprintMail',
                    'contreIndicationMail',
                ]
                const activeKeys = new Set(this.notificationSettings.map((s) => s.key))
                const payload = { ...this.localData }
                allNotificationTypes.forEach((type) => {
                    if (!activeKeys.has(type)) {
                        delete payload[type]
                        delete payload[this.getRoleKey(type)]
                    } else if (!payload[type]) {
                        // Le sélecteur de rôles est seulement masqué (v-if) quand le toggle est
                        // éteint : sans ça on renverrait les anciens destinataires, qui seraient
                        // ré-attachés derrière une notification désactivée.
                        payload[this.getRoleKey(type)] = []
                    }
                })

                const response = await this.axios.post(`/back-office/v3/mailcustom/${this.data.id}/edit`, payload)
                if (response.data.success) {
                    this.$toast.success(response.data.message)
                } else {
                    this.$toast.error(response.data.message || this.$t('common.generic_error'))
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                if (error.response?.data?.errors) {
                    const errors = error.response.data.errors
                    Object.keys(errors).forEach((key) => {
                        this.$toast.error(errors[key][0])
                    })
                } else {
                    this.$toast.error(this.$t('common.save_error'))
                }
            } finally {
                this.saving = false
            }
        },
    },
}
</script>

<style scoped>
.mail-custom-v3 {
    padding: 20px;
}
</style>
